import { MouseEvent, ReactNode, useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { createAggregatedListing } from 'src/components/Listings/listingUtils';
import {
  clampPageSize,
  PageSizeOption,
  PageSizeSelector,
} from 'src/components/Selectors/PageSizeSelector/PageSizeSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { InputPriceFocusProvider } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import {
  reorderByIds,
  useSearchDataContextWithCatalogFallback,
} from 'src/contexts/SearchDataContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import { ColumnSettingsModal } from 'src/modals/ColumnSettings/ColumnSettings';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { LayoutIcon } from 'src/svgs';
import { ListingWithEvent } from 'src/tables/ListingTable/ListingTable.types';
import {
  ListingTableFlattened,
  ListingTableFlattenedProps,
} from 'src/tables/ListingTable/ListingTableFlattened';
import {
  ListingTableFlattenedV2,
  ListingTableFlattenedV2Props,
} from 'src/tables/ListingTable/ListingTableFlattenedV2';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { isInSearchPage } from 'src/utils/deepLinkUtils';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { Listing, ListingQuery, ListingQueryV2 } from 'src/WebApiController';

import { bulkToolbarContainer } from '../EventListing.css';
import { InventoryGlobalBulkActions } from './BulkActions';
import * as styles from './InventoryFlattenedView.css';
import { SetInventoryFlattenedViewSelectionCountLabel } from './SetInventoryFlattenedViewSelectionCountLabel';

type InventoryFlattenedViewProps = {
  before?: ReactNode;
  topListItemBefore?: ReactNode;
  /**
   * If provided, the list will scroll to the month containing the event with the given posEventId
   */
  scrollToEventId?: string;
  isSearchV2?: boolean;
  doNotShowToolbar?: boolean;
  pageSize?: PageSizeOption;
  onPageSizeChange?: (newSize: PageSizeOption) => void;
  /** The persisted default page size. Used to show the filled pin icon on the correct option. */
  pinnedPageSize?: PageSizeOption;
  /** Called when the user pins a new page size as their persistent default. */
  onPinPageSize?: (newSize: PageSizeOption) => void;
} & Pick<
  ListingTableFlattenedProps,
  | 'alwaysShowCheckbox'
  | 'showSelectAllOnHeader'
  | 'enableColumnFilters'
  | 'ignoreMaxCount'
  | 'groupingStateReadonly'
  | 'columnVisibilityOverride'
> & {
    useTableV2?: boolean;
    /**
     * Only used when useTableV2 is true.
     * If true, the pagination will be handled by the server.
     * This means we have to have pagination (disablePagination will be overridden to false)
     * and we'll listen to pagination and sorting changes and pass them back to the server.
     *
     * requires `onPaginationChange` and `onSortingChange` and `serverSidePageIndex` to be provided.
     */
    serverSidePagination?: boolean;
  } & Pick<
    ListingTableFlattenedV2Props,
    'onPaginationChange' | 'onSortingChange' | 'serverSidePageIndex'
  >;

export const InventoryFlattenedView = ({
  before,
  topListItemBefore,
  doNotShowToolbar,
  serverSidePagination = false,
  isSearchV2,
  useTableV2 = false,
  pageSize,
  onPageSizeChange,
  pinnedPageSize,
  onPinPageSize,
  ...rest
}: InventoryFlattenedViewProps) => {
  const { mainRoute } = useAppContext();
  const isSearchPage = isInSearchPage(mainRoute);

  const { filterQuery } = useFilterQueryContext<ListingQuery>();

  const [isColumnModalOpen, toggleColumnModal] = useToggle(false);

  const hasTableTotalsFeature = useUserHasTableTotalsFeature();

  const onColumnSettingButtonClickHandler = (
    e: MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (!isColumnModalOpen) toggleColumnModal(true);
  };

  const {
    data: catalog,
    eventsTransformed,
    totalItemsCount: totalServerSideRowCount,
    maxResultWindow,
    sortedItemIds,
  } = useSearchDataContextWithCatalogFallback();
  const { listingCount, ungroupedListingCount, listingGroupCount } =
    useMemo(() => {
      let listingCount = 0;
      let ungroupedListingCount = 0;
      let listingGroupCount = 0;

      (eventsTransformed ?? []).forEach((ev) => {
        listingCount += ev.counts.listCnt ?? 0;
        listingGroupCount += ev.counts.listGrpCnt ?? 0;
        ungroupedListingCount += ev.counts.ungrListCnt ?? 0;
      });

      return { listingCount, ungroupedListingCount, listingGroupCount };
    }, [eventsTransformed]);

  const mapListingWithEvent = useCallback(
    (l: Listing | null) => {
      const { event, performer, venue } = getEventPerformerVenue(
        l?.viagVirtId,
        catalog
      );

      if (event) {
        return { event: event.event, listing: l, performer, venue };
      }

      return null;
    },
    [catalog]
  );

  const listings = useMemo(() => {
    if (eventsTransformed?.every((ev) => ev.entities.listings == null)) {
      return null;
    }
    const flatListings = eventsTransformed
      ?.flatMap((ev) => ev.entities.listings)
      ?.filter((l): l is NonNullable<typeof l> => l != null);

    if (!flatListings) return null;

    const ordered = isSearchV2
      ? reorderByIds(flatListings, sortedItemIds)
      : flatListings;

    return ordered
      .map(mapListingWithEvent)
      .filter((l): l is NonNullable<typeof l> => l != null);
  }, [eventsTransformed, isSearchV2, mapListingWithEvent, sortedItemIds]);

  const failedToRetrieveData = useMemo(() => {
    if (!isSearchV2) {
      return false;
    }

    // Search V2 only - indicate that we failed to retrieve data for all the events
    // when getCatalogCount returns some events but no sales are found
    // This indicates issue that opensearch data is not matching catalog data
    return eventsTransformed?.some((ev) => ev.entities.getDataFail) ?? false;
  }, [eventsTransformed, isSearchV2]);

  const listingsSummaryByEvent = useMemo(() => {
    return eventsTransformed?.every((ev) => ev.entities.listingsSummary == null)
      ? undefined
      : eventsTransformed
          ?.map((ev) => ev.entities.listingsSummary)
          .map(mapListingWithEvent)
          .filter((l) => l != null)
          .map((l) => l!);
  }, [eventsTransformed, mapListingWithEvent]);

  const listingsSummary = useMemo(() => {
    if (isSearchV2) {
      // In Search V2, summary is within one of the events
      const summary = eventsTransformed?.find(
        (ev) => ev.entities.listingsSummary != null
      )?.entities.listingsSummary;

      return summary
        ? ({
            listing: summary as Listing,
          } as ListingWithEvent)
        : null;
    }

    if (hasTableTotalsFeature) {
      if (isSearchPage) {
        // TODO: Remove this once TableTotals_All/TableTotals_Inventory is GA'ed
        // Need the fallback for the case where the salesSummaryByEvent is null
        // because without TableTotals_* feature flag, the salesSummaryByEvent is null
        // but we still want to show the totals on search page
        return (
          createAggregatedListing(listingsSummaryByEvent) ??
          createAggregatedListing(
            listings
              ?.flatMap((l) => flattenListingGroup(l?.listing))
              .filter((l) => l?.isFull)
              .map((l) => mapListingWithEvent(l as Listing)!)
          )
        );
      }

      return createAggregatedListing(listingsSummaryByEvent);
    }
  }, [
    isSearchV2,
    hasTableTotalsFeature,
    eventsTransformed,
    isSearchPage,
    listingsSummaryByEvent,
    listings,
    mapListingWithEvent,
  ]);

  return (
    <Stack direction="column" height="full" width="full">
      {isColumnModalOpen ? (
        <ColumnSettingsModal<CustomListingColumn>
          onClose={toggleColumnModal}
          sectionType={SectionType.ListingsFlattened}
        />
      ) : null}
      <div className={styles.metricsAnimationUncollapsed}>{before}</div>
      {topListItemBefore}
      {!doNotShowToolbar && (
        <Stack
          gap="m"
          alignItems="center"
          justifyContent="spaceBetween"
          width="full"
          className={bulkToolbarContainer}
        >
          <SetInventoryFlattenedViewSelectionCountLabel>
            <MultiSelectActionBar style={{ padding: 0 }}>
              <InventoryGlobalBulkActions />
            </MultiSelectActionBar>
          </SetInventoryFlattenedViewSelectionCountLabel>
          <Button
            variant="textPlain"
            style={{ paddingRight: vars.spacing.xxs }}
            onClick={onColumnSettingButtonClickHandler}
          >
            <LayoutIcon size={vars.iconSize.m} />
            <Content id={ContentId.Columns} />
          </Button>
        </Stack>
      )}
      <InputPriceFocusProvider disablePagination pageSize={listingCount}>
        {useTableV2 ? (
          <ListingTableFlattenedV2
            {...rest}
            useVirtuoso
            disablePagination={!serverSidePagination}
            manualPagination={serverSidePagination}
            rowCount={
              serverSidePagination
                ? Math.min(
                    totalServerSideRowCount ?? 0,
                    maxResultWindow ?? Infinity
                  ) || undefined
                : undefined
            }
            listingCount={listingCount}
            ungroupedListingCount={ungroupedListingCount}
            listingGroupCount={listingGroupCount}
            listings={listings ?? undefined}
            listingsSummary={listingsSummary}
            failedToRetrieveData={failedToRetrieveData}
            pageSize={
              serverSidePagination
                ? ((filterQuery as ListingQueryV2)?.searchOptions?.take ??
                  undefined)
                : undefined
            }
            isSearchV2={isSearchV2}
            totalCount={
              serverSidePagination ? totalServerSideRowCount : undefined
            }
            footerCta={
              serverSidePagination &&
              pageSize != null &&
              onPageSizeChange != null ? (
                <PageSizeSelector
                  value={clampPageSize(pageSize)}
                  onChange={onPageSizeChange}
                  pinnedPageSize={pinnedPageSize}
                  onPinPageSize={onPinPageSize}
                />
              ) : undefined
            }
          />
        ) : (
          <ListingTableFlattened
            {...rest}
            useVirtuoso
            disablePagination
            listingCount={listingCount}
            ungroupedListingCount={ungroupedListingCount}
            listingGroupCount={listingGroupCount}
            listings={listings ?? undefined}
            listingsSummary={listingsSummary}
            failedToRetrieveData={false}
          />
        )}
      </InputPriceFocusProvider>
    </Stack>
  );
};
